import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { UntypedFormBuilder, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F1120SchNService } from './f1120-sch-n.service';

@Component({
    selector: 'gtw-f1120-sch-n',
    templateUrl: './f1120-sch-n.component.html',
    styleUrls: ['./f1120-sch-n.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1120SchNComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  filterParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  rowData: any = {};

  // Component properties migrated from F1120ScheduleNCtrl.js
  modalTitle = 'Foreign Operations Information';
  originalData: any[] = [];
  userSettings: any;
  logged_in_user: string = '';
  leId: string = '';
  formName = '1120';
  hgt = '96%';
  cdr_no: string = '';
  leType: string = '';
  systemFlag = 'D';
  showFormLoader = true;
  crudLoading = false;
  isSaveClicked = false;
  
  // Entity information
  entityList: any[] = [];

  // Form data structures (maintaining AngularJS structure)
  formData: any = {
    "addrows": [],
    "editrows": [],
    "removerows": []
  };
  
  scheduleNFormView: any[] = [];
  saveParamDtls: any[] = [];
  mainData: any[] = [];
  tableHeader: any[] = [];

  // Countries data
  allCountries: any[] = [];
  // Full code -> entry lookup, including LABEL-duplicates hidden from the dropdown.
  // Lets iterateArray() resolve a saved code that points to a deduped-out entry
  // (e.g. saved 'VN' when only 'VM' is shown for Vietnam).
  private countriesLookupByCode: Map<string, any> = new Map();
  CountriesList: any = {
    param_name: "Countries",
    values: [],
    label_name: "label",
    value_name: "value", 
    label: "Countries",
    selected: []
  };

  // Tab configuration (maintaining AngularJS structure)
  tabs = [
    { id: 1, name: "Form Entry", active: true }
  ];
  activeTab = 1;
  currentTabName = "Form Entry";
  
  // Modal minimization state
  minimise = false;

  // Multiselect dropdown state
  isCountryDropdownOpen = false;
  countrySearchText = '';

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f1120SchNService: F1120SchNService,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService = {
      USER_SETTINGS: header.userSettingsData || {},
      SERVER_CONFIG: header.serverConfigData || {},
    };
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.globalService.filterParams = this.filterParams;
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (!this.globalService || !this.GENERAL_CONFIG) {
        return;
      }

      this.userSettings = this.appService.USER_SETTINGS;
      this.logged_in_user = this.userSettings?.user?.sso_id || '';
      
      // 🔴 CRITICAL: Only proceed if we have row data - NO MOCKS!
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.setupEntityData();
        this.initSchN();
      }
    } catch (e) {
      console.error('Error in F1120SchN init:', e);
    }
  }

  private setupEntityData() {
    // 🔴 CRITICAL: Use REAL data from rowData - NO TEST_ values!
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.MARS;
    this.leType = this.rowData.LE_TYPE;
    
    this.entityList = [
      {key: "Tax Year", value: this.rowData.TAX_YEAR},
      {key: "LEID", value: this.rowData.LEID},
      {key: "LE Name", value: this.rowData.LE_NAME}
    ];
  }

  // Initialize Schedule N form
  initSchN() {
    this.fetchCountryList().then(() => {
      this.fetchSchNrecords();
    });
  }

  // Fetch countries list
  fetchCountryList(): Promise<any> {
    const params = {};

    return firstValueFrom(
      this.jsonObjectFactory.getJSONObj(
        this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=kgdgoh',
        params,
        this.globalService,
        null,
        null
      )
    )
      .then((data: any) => {
      this.allCountries = [];
      
      data.jsonObject.forEach((country: any, index: number) => {
        const obj = {
          LABEL: country.COUNTRY_DESC,
          VALUE: country.COUNTRY_CODE
        };
        this.allCountries.push(obj);
      });

      // Backend (kgdgoh) returns both legacy IRS and ISO codes for some countries
      // on certain client_keys (e.g. AG+DZ Algeria, VM+VN Vietnam). Dedupe by LABEL,
      // keeping the first occurrence (response is alphabetical by description, so
      // the IRS code typically appears first). Full code lookup is preserved so saved
      // values still resolve to a displayed row.
      this.countriesLookupByCode = new Map(
        this.allCountries.map((c: any) => [c.VALUE, c])
      );
      const seenLabels = new Set<string>();
      const dedupedCountries = this.allCountries.filter((c: any) => {
        if (seenLabels.has(c.LABEL)) {
          return false;
        }
        seenLabels.add(c.LABEL);
        return true;
      });

      // Initialize CountriesList with empty selected array
      this.CountriesList = {
        param_name: "Countries",
        values: dedupedCountries,
        label_name: "label",
        value_name: "value",
        label: "Countries",
        selected: []
      };
      this.cdr.markForCheck();
    });
  }

  // 🔴 CRITICAL: API calls use REAL rowData - NO globalParams or 'BASE'!
  fetchSchNrecords() {
    const params = { 
      "action_id": 31333, 
      "tax_year": this.rowData.TAX_YEAR,           // NOT hardcoded year
      "scenario": this.rowData.SCENARIO,           // NOT 'BASE'
      "jcd_key": this.rowData.JCD_KEY,            // NOT globalParams.jcd_key
      "combination_key": this.rowData.COMBINATION_KEY,
      "form_name": "F1120", 
      "schedule_name": "N", 
      "part_no": "NA"
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadPivotFormJson',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.processFormData(data);
      this.showFormLoader = false;
      this.cdr.markForCheck();
    });
  }

  private processFormData(data: any) {
    const scheduleNForm: { rows: any[] } = { rows: [] };
    let saveParamDtls: any[] = [];
    let tempDataObj: any = {};

    for (let i = 1; i < data.irsForm.formItemList.length; i++) {
      saveParamDtls = [];
      tempDataObj = {};
      saveParamDtls = data.irsForm.formItemList[i].dataRowList[0].rowDimensions.object_id.split('~');
      const rows = data.irsForm.formItemList[i].dataRowList[0].dataCellList.length;
      
      tempDataObj.num = data.irsForm.formItemList[i].dataRowList[0].dataCellList[0].name;
      tempDataObj.description = data.irsForm.formItemList[i].dataRowList[0].dataCellList[1].name;
      
      tempDataObj.line_order = saveParamDtls[8];
      tempDataObj.saveParams = saveParamDtls;
      
      if (rows > 2) {
        this.iterateArray(rows, data.irsForm.formItemList[i].dataRowList[0].dataCellList, tempDataObj);
      }
      
      scheduleNForm.rows.push(tempDataObj);
      // modal title
      this.modalTitle = data.irsForm.formItemList[0].dataRowList[0].dataCellList[1].name;
    }

    this.tableHeader = scheduleNForm.rows.filter((obj: any) => obj.num === "NA");
    this.scheduleNFormView = scheduleNForm.rows.filter((obj: any) => obj.num !== 'NA');
    
    if (this.tableHeader.length > 0) {
      this.tableHeader[0].value = this.rowData.LE_NAME;
      if (this.tableHeader.length > 1) {
        this.tableHeader[1].value = this.rowData.EIN;
      }
    }
    
    this.originalData = _.cloneDeep(this.scheduleNFormView);
  }

  // Process different column counts in form data
  private iterateArray(count: number, array: any[], tempDataObj: any) {
    if (count === 3) {
      tempDataObj.attrib_name_a = array[2].key.split('~')[0];
      tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
      tempDataObj.attrib_order_a = array[2].key.split('~')[2];
      tempDataObj.edit_type = array[2].property.edit_type;
      tempDataObj.data_type = array[2].property.data_type;
      
      if (tempDataObj.edit_type === "multi_select") {
        if (array[2].value == null) {
          tempDataObj.value = [];
          this.CountriesList.selected = [];
        } else {
          const x = array[2].value.split(',');
          // Clear existing selections to avoid duplicates
          this.CountriesList.selected = [];
          
          if (this.CountriesList.values && this.CountriesList.values.length > 0) {
            x.forEach((countryCode: string) => {
              const trimmed = countryCode.trim();
              let displayCountry = this.CountriesList.values.find((country: any) => country.VALUE === trimmed);
              if (!displayCountry) {
                // Saved code maps to a duplicate hidden by dedupe (e.g. 'VN' -> Vietnam, displayed as 'VM').
                const fullEntry = this.countriesLookupByCode.get(trimmed);
                if (fullEntry) {
                  displayCountry = this.CountriesList.values.find((country: any) => country.LABEL === fullEntry.LABEL);
                }
              }
              if (displayCountry && !this.CountriesList.selected.some((selected: any) => selected.VALUE === displayCountry.VALUE)) {
                this.CountriesList.selected.push(displayCountry);
              }
            });
          }
          tempDataObj.value = this.CountriesList.selected;
        }
      } else if (tempDataObj.data_type === "number") {
        tempDataObj.value = array[2].value;
      } else if (array[2].value == null) {
        tempDataObj.value = '';
      }
    } else if (count === 4) {
      tempDataObj.attrib_name_a = array[2].key.split('~')[0];
      tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
      tempDataObj.attrib_order_a = array[2].key.split('~')[2];

      tempDataObj.attrib_name_b = array[3].key.split('~')[0];
      tempDataObj.lineAttrKey_b = array[3].key.split('~')[1];
      tempDataObj.attrib_order_b = array[3].key.split('~')[2];
      
      tempDataObj.edit_type = array[2].property.edit_type;
      tempDataObj.data_type = array[2].property.data_type;

      if (array[2].value === "true") {
        tempDataObj.value = true;
      } else if (array[3].value === "true" || array[2].value === "false") {
        tempDataObj.value = false;
      } else {
        tempDataObj.value = 'NA';
      }
    }
  }

  // Handle field visibility changes for conditional fields
  fieldVisibility(index: number, state: boolean, name: string, rowNum: string) {
    this.scheduleNFormView[index].conditionalField = !this.scheduleNFormView[index].conditionalField;
    if ((name === 'yes' && !state) || (name === 'no' && state)) {
      this.scheduleNFormView[index].value = 'NA';
    }
  }

  // Handle countries selection
  onCountriesChange(selectedCountries: any[]) {
    this.CountriesList.selected = selectedCountries;
  }

  // Multiselect dropdown methods
  toggleCountryDropdown(event?: Event) {
    event?.stopPropagation();
    this.isCountryDropdownOpen = !this.isCountryDropdownOpen;
    this.cdr.markForCheck();
  }

  getCountryHeaderText(): string {
    if (!this.CountriesList.selected || this.CountriesList.selected.length === 0) {
      return 'Select Countries';
    }
    if (this.CountriesList.selected.length === 1) {
      return this.CountriesList.selected[0].LABEL;
    }
    return `${this.CountriesList.selected.length} countries selected`;
  }

  getFilteredCountries(): any[] {
    if (!this.countrySearchText) {
      return this.CountriesList.values || [];
    }
    return (this.CountriesList.values || []).filter((country: any) =>
      country.LABEL.toLowerCase().includes(this.countrySearchText.toLowerCase())
    );
  }

  isCountrySelected(country: any): boolean {
    return this.CountriesList.selected && 
           this.CountriesList.selected.some((selected: any) => selected.VALUE === country.VALUE);
  }

  toggleCountrySelection(country: any) {
    if (!this.CountriesList.selected) {
      this.CountriesList.selected = [];
    }

    const index = this.CountriesList.selected.findIndex((selected: any) => selected.VALUE === country.VALUE);
    
    if (index > -1) {
      // Remove country if already selected
      this.CountriesList.selected.splice(index, 1);
    } else {
      // Add country if not selected
      this.CountriesList.selected.push(country);
    }

    // Update the form data
    this.onCountriesChange(this.CountriesList.selected);
  }

  selectAllCountries() {
    this.CountriesList.selected = [...this.getFilteredCountries()];
    this.onCountriesChange(this.CountriesList.selected);
  }

  unselectAllCountries() {
    this.CountriesList.selected = [];
    this.onCountriesChange(this.CountriesList.selected);
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    const dropdown = target.closest('.multiselect-container');
    
    if (!dropdown && this.isCountryDropdownOpen) {
      this.isCountryDropdownOpen = false;
      this.cdr.markForCheck();
    }
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    // Build save data with real rowData values (like F7004 pattern)
    const tempArray: any[] = [];
    let selectedCountry = "";
    
    // Handle countries selection
    if (this.CountriesList.selected != null && this.CountriesList.selected.length > 0) {
      for (let l = 0; l < this.CountriesList.selected.length; l++) {
        selectedCountry = selectedCountry + this.CountriesList.selected[l].VALUE + ',';
      }
      selectedCountry = selectedCountry.slice(0, -1);
      
      for (let i = 0; i < this.scheduleNFormView.length; i++) {
        if (this.scheduleNFormView[i].num === '6b') {
          this.scheduleNFormView[i].value = selectedCountry;
        }
      }
    }
    
    for (let i = 0; i < this.scheduleNFormView.length; i++) {
      const formRowDataA = {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: this.scheduleNFormView[i].saveParams[1],
        line_order: this.scheduleNFormView[i].line_order,
        attrib_order: this.scheduleNFormView[i].attrib_order_a,
        line_description: this.scheduleNFormView[i].description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: this.scheduleNFormView[i].saveParams[2],
        line_no: this.scheduleNFormView[i].num,
        occurence: this.scheduleNFormView[i].saveParams[7],
        line_attrib_key: this.scheduleNFormView[i].lineAttrKey_a,
        Attribute_Name: this.scheduleNFormView[i].attrib_name_a,
        Attribute_Value: this.scheduleNFormView[i].value === true ? 'true' : 
                        this.scheduleNFormView[i].value === 'NA' ? 'false' : 
                        this.scheduleNFormView[i].value === false ? 'false' : 
                        this.scheduleNFormView[i].value ? this.scheduleNFormView[i].value.toString() : '',
        trans_dtls_key: this.scheduleNFormView[i].saveParams[3],
        trans_status: this.scheduleNFormView[i].saveParams[6],
        is_dirty: ''
      };
      tempArray.push(formRowDataA);

      if (typeof this.scheduleNFormView[i].attrib_name_b !== "undefined") {
        const formRowDataB = {
          tax_year: this.rowData.TAX_YEAR,
          trans_type_key: this.scheduleNFormView[i].saveParams[1],
          line_order: this.scheduleNFormView[i].line_order,
          attrib_order: this.scheduleNFormView[i].attrib_order_b,
          line_description: this.scheduleNFormView[i].description,
          combination_key: this.rowData.COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: this.scheduleNFormView[i].saveParams[2],
          line_no: this.scheduleNFormView[i].num,
          occurence: this.scheduleNFormView[i].saveParams[7],
          line_attrib_key: this.scheduleNFormView[i].lineAttrKey_b,
          Attribute_Name: this.scheduleNFormView[i].attrib_name_b,
          Attribute_Value: this.scheduleNFormView[i].value === true ? 'false' : 
                          this.scheduleNFormView[i].value === 'NA' ? 'false' : 
                          this.scheduleNFormView[i].value === false ? 'true' : 
                          this.scheduleNFormView[i].value ? this.scheduleNFormView[i].value.toString() : '',
          trans_dtls_key: this.scheduleNFormView[i].saveParams[3],
          trans_status: this.scheduleNFormView[i].saveParams[6],
          is_dirty: ''
        };
        tempArray.push(formRowDataB);
      }
    }

    this.formData.editrows = [tempArray];
    this.mainData = [this.formData];

    // 🚀 CRITICAL: Pass filterParams and rowData to service (F7004 pattern)
    this.f1120SchNService.saveF1120SchNForm(
      this.GENERAL_CONFIG.base_url,  // 🚀 Use base_url (contains /gtw/)
      this.mainData,
      this.globalService,
      this.filterParams,  // 🚀 Pass dynamic filter params
      this.rowData        // 🚀 Pass row data
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        
        if (response.callSuccess === "1" && response.errorMessage === "null") {
          // 🚀 CRITICAL: Show snackBar and emit 'refresh' string (F7004 pattern)
          this.gtwSnackbar.openSnackBar('Form has been successfully saved/updated', 'gtw-snackbar--green', true, 4);
          
          // 🚀 CRITICAL: Emit 'refresh' string (NOT object)
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close
        } else {
          if (response.errorMessage && response.errorMessage !== 'null') {
            this.gtwSnackbar.openSnackBar(response.errorMessage, 'gtw-snackbar--red', true, 4);
          } else {
            this.gtwSnackbar.openSnackBar('Error occurred in saving the Transaction. If this continues please contact support', 'gtw-snackbar--red', true, 4);
          }
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.gtwSnackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red', true, 4);
        this.cdr.markForCheck();
      }
    );
  }

  reset() {
    this.scheduleNFormView = _.cloneDeep(this.originalData);
  }

  cancel() {
    // Emit cancel event to close modal (AngularJS integration)
    this.requestGridUpdate.emit('cancel');
  }

  // Handle radio button changes (fixed checkbox issue)
  onRadioChange(index: number, value: boolean, row: any) {
    row.value = value;
    this.fieldVisibility(index, value, row.description, row.num);
  }

  // Track by function for ngFor performance
  itemTracker(index: number, item: any): any {
    return item ? item.line_attrib_key || item.LEID || index : index;
  }
}